import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { Flame, Info } from "lucide-react";

import { TagChip } from "@/components/tag-chip";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { getTagTrendBreakdown } from "@/lib/tag-trend.functions";
import { TAG_SIGNAL_LABELS, tagLabel, type TagSummary, type TagTrendSignal } from "@/lib/tags";

/**
 * A trending hashtag that explains itself: hovering — or focusing the "why"
 * button and pressing Enter — shows which recent reads, likes, saves, comments
 * and new publications produced the score once time decay is applied.
 */
export function TrendingTagChip({
  tag,
  score,
  days = 14,
}: {
  tag: Pick<TagSummary, "id" | "slug" | "name">;
  score?: number | null;
  days?: number;
}) {
  const [open, setOpen] = React.useState(false);
  const panelId = React.useId();

  const breakdown = useQuery({
    queryKey: ["tag-trend-breakdown", tag.id, days],
    queryFn: async () =>
      (await getTagTrendBreakdown({ data: { tagId: tag.id, days } })).rows as TagTrendSignal[],
    enabled: open,
    staleTime: 60_000,
  });

  const rows = breakdown.data ?? [];
  const total = rows.reduce((sum, row) => sum + Number(row.points), 0);

  const summary =
    rows.length === 0
      ? `No recent activity recorded for ${tagLabel(tag)} in the last ${days} days.`
      : `${tagLabel(tag)} scores ${(typeof score === "number" ? score : total).toFixed(1)} from ` +
        rows
          .map((row) => `${row.events} ${TAG_SIGNAL_LABELS[row.signal] ?? row.signal}`)
          .join(", ") +
        ` in the last ${days} days.`;

  return (
    <HoverCard openDelay={120} closeDelay={80} open={open} onOpenChange={setOpen}>
      <span className="inline-flex items-center gap-1">
        <TagChip tag={tag} size="md" />
        <HoverCardTrigger asChild>
          <button
            type="button"
            aria-expanded={open}
            aria-controls={open ? panelId : undefined}
            aria-label={`Why ${tagLabel(tag)} is trending`}
            className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-border/70 text-muted-foreground transition-smooth hover:border-primary/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={() => setOpen((value) => !value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setOpen((value) => !value);
              }
              if (event.key === "Escape") setOpen(false);
            }}
          >
            <Info className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </HoverCardTrigger>
      </span>

      {/* Screen readers get the same explanation as a plain sentence. */}
      <span className="sr-only" role="status" aria-live="polite">
        {open && !breakdown.isLoading ? summary : ""}
      </span>

      <HoverCardContent
        id={panelId}
        role="dialog"
        aria-label={`Why ${tagLabel(tag)} is trending`}
        align="start"
        className="w-72"
        onEscapeKeyDown={() => setOpen(false)}
      >
        <p className="flex items-center gap-1.5 text-sm font-medium text-foreground">
          <Flame className="h-3.5 w-3.5 text-primary-glow" aria-hidden="true" />
          Why {tagLabel(tag)} is trending
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Activity from the last {days} days, weighted so today counts twice as much as {days} days
          ago.
        </p>

        {breakdown.isLoading ? (
          <p className="mt-3 text-xs text-muted-foreground">Working out the breakdown…</p>
        ) : rows.length === 0 ? (
          <p className="mt-3 text-xs text-muted-foreground">
            No recent activity recorded in this window.
          </p>
        ) : (
          <ul className="mt-3 space-y-1.5">
            {rows.map((row) => {
              const share = total > 0 ? Math.round((Number(row.points) / total) * 100) : 0;
              return (
                <li key={row.signal} className="text-xs">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-foreground">
                      {row.events} {TAG_SIGNAL_LABELS[row.signal] ?? row.signal}
                    </span>
                    <span className="text-muted-foreground">
                      {Number(row.points).toFixed(1)} pts · {share}%
                    </span>
                  </div>
                  <div
                    className="mt-1 h-1 overflow-hidden rounded-full bg-border/70"
                    aria-hidden="true"
                  >
                    <div className="h-full rounded-full bg-primary/70" style={{ width: `${share}%` }} />
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <p className="mt-3 border-t border-border/60 pt-2 text-[11px] text-muted-foreground">
          Score {typeof score === "number" ? score.toFixed(1) : total.toFixed(1)} · weights: new
          publication 6, save 5, like 4, comment 3, read 2.
        </p>
      </HoverCardContent>
    </HoverCard>
  );
}
